import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Intent } from '@blueprintjs/core';
import { format, formatDistanceToNow } from 'date-fns';

import { type Scopes } from '../../auth';
import { baseUrl } from '../../config';
import { Notifications } from '../../notifications/components/Notifications';
import { type Settings } from '../../settings';
import { LoadingError, type RequestError, ResourceNotFound } from '../../shared';
import { observableToPromise } from '../../shared/observableToPromise';
import { Actions } from '../actions';
import { type MemberResponse as ApiMemberResponse, getAccountMembers, searchMembers } from '../api';
import { CSVExport } from '../components/CSVExport';
import { MembersPanel } from '../components/MembersPanel';
import { useAccountQuery } from '../hooks/useAccountQuery';
import { useMembers } from '../hooks/useMembers';
import {
  type Account,
  type CustomRole,
  getAccountDisplayName,
  getMemberDisplayName,
  type Member,
  type MemberSessions,
  type MembersPaginationData,
  type TeamsData,
} from '../model';

export const FILTER_MIN_LENGTH = 2;
// A filter shows at most this many matches. We scan the account page by page and
// stop as soon as we've collected this many, so we never load the whole account
// just to filter it.
export const FILTERED_RESULTS_LIMIT = 20;
// Page size for the sequential scan. Large enough that a filter with plenty of
// matches is usually satisfied by a single request (the members API accepts up
// to 1000), while still stopping early once we have FILTERED_RESULTS_LIMIT.
export const MEMBERS_FETCH_PAGE_SIZE = 1000;

type MemberSearchIndex = {
  role: string;
  customRoleKeys: string[];
  teamKeys: string[];
  teamCustomRoleKeys: string[];
  email: string;
  firstName: string;
  lastName: string;
};

export type IndexedMember = Member & { _searchIndex: MemberSearchIndex };

export function buildSearchIndex(
  member: Member,
  customRolesMap: { [id: string]: CustomRole } | undefined,
): MemberSearchIndex {
  return {
    role: (member.role || '').toLowerCase(),
    customRoleKeys:
      member.customRoles && customRolesMap
        ? member.customRoles.map((roleId) => customRolesMap[roleId]?.key?.toLowerCase() || '').filter(Boolean)
        : [],
    teamKeys: member.teams ? member.teams.map((team: TeamsData) => (team.key || '').toLowerCase()).filter(Boolean) : [],
    teamCustomRoleKeys:
      member.teams && member.teams.length > 0
        ? member.teams
            .flatMap((team: TeamsData) => team.customRoleKeys || [])
            .map((key) => key.toLowerCase())
            .filter(Boolean)
        : [],
    email: (member.email || '').toLowerCase(),
    firstName: (member.firstName || '').toLowerCase(),
    lastName: (member.lastName || '').toLowerCase(),
  };
}

// Splits a comma-separated filter into individual terms. All whitespace is
// stripped (surrounding and internal), and terms shorter than FILTER_MIN_LENGTH
// are dropped so stray commas / blanks (e.g. "reader, ,writer") are ignored.
export function parseFilterTerms(raw: string): string[] {
  return raw
    .split(',')
    .map((term) => term.replace(/\s+/g, '').toLowerCase())
    .filter((term) => term.length >= FILTER_MIN_LENGTH);
}

export function filterIndexedMembers(
  members: IndexedMember[],
  options: {
    roleFilter: string;
    teamFilter: string;
    searchTerm: string;
    enableTeamsVisibility: string | boolean;
  },
): { filtered: IndexedMember[]; totalCount: number } {
  let filtered = members;
  // Role and team filters support multiple comma-separated terms: a member
  // matches a field if it matches ANY term (OR within a field), and the role,
  // team, and search filters are combined with AND across fields.
  const roleTerms = parseFilterTerms(options.roleFilter);
  const teamTerms = parseFilterTerms(options.teamFilter);
  const searchTermLower = options.searchTerm.trim().toLowerCase();

  if (roleTerms.length > 0) {
    // Match what vital actually displays in the Role column: when a member has
    // their own custom roles, those take precedence and the base role is hidden,
    // so we don't match on it. When they have no custom roles, we match the
    // built-in base role (e.g. "reader"). Team-inherited custom role keys are
    // always matched (they're shown separately in the Team column).
    filtered = filtered.filter((member) =>
      roleTerms.some((term) => {
        const hasOwnCustomRoles = member._searchIndex.customRoleKeys.length > 0;
        if (!hasOwnCustomRoles && member._searchIndex.role.includes(term)) {
          return true;
        }
        if (member._searchIndex.customRoleKeys.some((key) => key.includes(term))) {
          return true;
        }
        if (
          options.enableTeamsVisibility !== 'teams_off' &&
          member._searchIndex.teamCustomRoleKeys.some((key) => key.includes(term))
        ) {
          return true;
        }
        return false;
      }),
    );
  }

  if (teamTerms.length > 0) {
    filtered = filtered.filter((member) =>
      teamTerms.some((term) => member._searchIndex.teamKeys.some((key) => key.includes(term))),
    );
  }

  if (searchTermLower) {
    filtered = filtered.filter(
      (member) =>
        member._searchIndex.email.includes(searchTermLower) ||
        member._searchIndex.firstName.includes(searchTermLower) ||
        member._searchIndex.lastName.includes(searchTermLower),
    );
  }

  return { filtered, totalCount: filtered.length };
}

type OwnProps = Readonly<{
  account: Account;
  settings: Settings;
  scopes: Scopes;
  memberSessions: MemberSessions[];
  customRoles: { [accountId: string]: CustomRole };
  onSetMfaDisabled: (account: Account, member: Member) => void;
  navigate?: (path: string) => void;
  marketplaceProvider?: string;
}>;
export default function MembersPanelContainer({
  account,
  settings,
  scopes,
  memberSessions,
  customRoles,
  onSetMfaDisabled,
  navigate,
  marketplaceProvider,
}: OwnProps) {
  const dispatch = useDispatch();

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [teamFilter, setTeamFilter] = useState<string>('');

  const [pageUrl, setPageUrl] = useState<string | undefined>(() => {
    const offsetFromQueryParams = new URL(window.location.href).href.split('offset=')[1];
    if (typeof offsetFromQueryParams === 'string') {
      return `/api/v2/members?limit=20&offset=${offsetFromQueryParams}`;
    }
    return undefined;
  });

  const enablePagination = settings.enablePaginationOfMembers;

  const hasActiveFilters = useMemo(
    () =>
      settings.enableRoleAndTeamFilter &&
      (roleFilter.trim().length >= FILTER_MIN_LENGTH || teamFilter.trim().length >= FILTER_MIN_LENGTH),
    [settings.enableRoleAndTeamFilter, roleFilter, teamFilter],
  );

  // Default/paginated list
  const membersListQuery = useMembers(account, {
    url: pageUrl,
    paginated: enablePagination,
    enabled: searchTerm.trim().length === 0 && !hasActiveFilters,
  });

  // Non-paginated full list for CSV export. Called unconditionally (hooks
  // must run in the same order every render); gated by 'enabled' instead of
  // by only calling the hook when settings.enableMemberSessions is true.
  const exportQuery = useMembers(account, { paginated: false, enabled: settings.enableMemberSessions });

  // Up to FILTERED_RESULTS_LIMIT matching members, collected by the scan below.
  const [filteredMembers, setFilteredMembers] = useState<Member[]>([]);
  // True when we stopped scanning because we hit the cap (there may be more matches).
  const [filterHitCap, setFilterHitCap] = useState(false);
  const [isFetchingAll, setIsFetchingAll] = useState(false);
  const [fetchProgress, setFetchProgress] = useState<{ current: number; total: number } | null>(null);
  const accountId = account?._id;
  const roleAndTeamFilterEnabled = settings.enableRoleAndTeamFilter;
  const [memberFilterScope, setMemberFilterScope] = useState({
    accountId,
    roleAndTeamFilterEnabled,
  });
  if (
    memberFilterScope.accountId !== accountId ||
    memberFilterScope.roleAndTeamFilterEnabled !== roleAndTeamFilterEnabled
  ) {
    setMemberFilterScope({ accountId, roleAndTeamFilterEnabled });
    if (roleAndTeamFilterEnabled) {
      setFilteredMembers([]);
      setFilterHitCap(false);
      setIsFetchingAll(false);
      setFetchProgress(null);
      setSearchTerm('');
      setRoleFilter('');
      setTeamFilter('');
    }
  }

  const clearFilterResults = useCallback(() => {
    setFilteredMembers([]);
    setFilterHitCap(false);
    setIsFetchingAll(false);
    setFetchProgress(null);
  }, []);

  const scanKey = hasActiveFilters && account ? `${account._id}:${roleFilter}:${teamFilter}:${searchTerm}` : '';
  const [appliedScan, setAppliedScan] = useState({
    scanKey,
    customRoles,
    teamsVisibility: settings.enableTeamsVisibility,
  });
  if (
    appliedScan.scanKey !== scanKey ||
    appliedScan.customRoles !== customRoles ||
    appliedScan.teamsVisibility !== settings.enableTeamsVisibility
  ) {
    setAppliedScan({ scanKey, customRoles, teamsVisibility: settings.enableTeamsVisibility });
    if (scanKey === '') {
      setFilteredMembers([]);
      setFilterHitCap(false);
      setIsFetchingAll(false);
      setFetchProgress(null);
    } else {
      setIsFetchingAll(true);
      setFetchProgress(null);
    }
  }

  // When a filter is active, scan the account page by page, filtering each page
  // with the same client-side semantics, and stop as soon as we've collected
  // FILTERED_RESULTS_LIMIT matches. This bounds load: a filter that matches
  // plenty is usually satisfied by the first page. Re-runs when the filter (or
  // account / custom roles) changes; the previous scan is aborted.
  useEffect(() => {
    if (!hasActiveFilters || !account) {
      return undefined;
    }

    const abortController = new AbortController();

    const fetchPage = async (offset: number): Promise<{ members: Member[]; totalCount: number }> => {
      const url = `/api/v2/members?limit=${MEMBERS_FETCH_PAGE_SIZE}&offset=${offset}&expand=roleAttributes`;
      const response: ApiMemberResponse | null = await observableToPromise(
        getAccountMembers(account, true, url),
        abortController.signal,
      );
      if (!response || !response.entities?.members) {
        return { members: [], totalCount: 0 };
      }
      return {
        members: Object.values(response.entities.members) as Member[],
        totalCount: response.result?.totalCount ?? 0,
      };
    };

    const scan = async () => {
      try {
        const matches: Member[] = [];
        let offset = 0;
        let scanned = 0;
        let totalCount = 0;
        let hitCap = false;

        do {
          // oxlint-disable-next-line no-await-in-loop
          const { members: pageMembers, totalCount: pageTotal } = await fetchPage(offset);
          if (abortController.signal.aborted) {
            return;
          }
          if (offset === 0) {
            totalCount = pageTotal;
          }
          if (pageMembers.length === 0) {
            break;
          }
          scanned += pageMembers.length;

          // Filtering is per-member, so filtering page by page yields the same
          // matches as filtering the whole set — we just stop early.
          const indexed: IndexedMember[] = pageMembers.map((m) => ({
            ...m,
            _searchIndex: buildSearchIndex(m, customRoles),
          }));
          const { filtered } = filterIndexedMembers(indexed, {
            roleFilter,
            teamFilter,
            searchTerm,
            enableTeamsVisibility: settings.enableTeamsVisibility as string | boolean,
          });
          for (const match of filtered) {
            if (matches.length >= FILTERED_RESULTS_LIMIT) {
              break;
            }
            matches.push(match);
          }

          setFetchProgress({ current: scanned, total: totalCount || scanned });

          if (matches.length >= FILTERED_RESULTS_LIMIT) {
            // Stopped early: more matches may exist beyond what we scanned.
            hitCap = scanned < totalCount || filtered.length > FILTERED_RESULTS_LIMIT;
            break;
          }
          offset += MEMBERS_FETCH_PAGE_SIZE;
        } while (offset < totalCount && !abortController.signal.aborted);

        if (!abortController.signal.aborted) {
          setFilteredMembers(matches);
          setFilterHitCap(hitCap);
          setIsFetchingAll(false);
          setFetchProgress(null);
        }
      } catch (error) {
        console.error('Error scanning members for filter:', error);
        if (!abortController.signal.aborted) {
          clearFilterResults();
        }
      }
    };

    scan().catch((err) => console.error('Member filter scan error:', err));

    return () => {
      abortController.abort();
    };
  }, [
    hasActiveFilters,
    account,
    roleFilter,
    teamFilter,
    searchTerm,
    customRoles,
    settings.enableTeamsVisibility,
    clearFilterResults,
  ]);

  // Search results
  const searchQuery = useAccountQuery<ApiMemberResponse | null, RequestError>(
    ['accounts', 'members-search', account?._id, searchTerm.trim()],
    ({ signal }) =>
      account && searchTerm.trim().length > 0
        ? observableToPromise(searchMembers(account, searchTerm.trim()), signal)
        : Promise.resolve(null),
    account,
    { enabled: searchTerm.trim().length > 0 && !hasActiveFilters, staleTime: 10_000 },
  );

  const isFetching = Boolean(membersListQuery.isLoading || searchQuery.isLoading || isFetchingAll);
  const error: RequestError | null = membersListQuery.error || searchQuery.error || null;

  const listData = membersListQuery.data;
  const searchData = searchQuery.data;

  const members: Member[] = useMemo(() => {
    if (hasActiveFilters) {
      return filteredMembers;
    }

    if (searchTerm.trim().length > 0) {
      const searchEntities = searchData?.entities?.members;
      return searchEntities ? (Object.values(searchEntities) as Member[]) : [];
    }

    const entitiesMembers = listData?.entities?.members;
    return entitiesMembers ? (Object.values(entitiesMembers) as Member[]) : [];
  }, [hasActiveFilters, filteredMembers, searchTerm, listData, searchData]);

  const paginationData: MembersPaginationData = useMemo(() => {
    if (listData && listData.result) {
      return {
        links: listData.result._links,
        totalCount: listData.result.totalCount,
      };
    }
    return {
      // When data hasn't loaded yet, provide an empty links object to keep controls disabled
      links: {} as MembersPaginationData['links'],
      totalCount: 0,
    };
  }, [listData]);

  const sortedMembers = useMemo(
    () =>
      members.slice().sort((a, b) => {
        if (a.role === 'owner' && b.role !== 'owner') {
          return -1;
        }
        if (a.role !== 'owner' && b.role === 'owner') {
          return 1;
        }
        return 0;
      }),
    [members],
  );

  if (error) {
    const { status } = error;
    switch (status) {
      case 404:
        return (
          <ResourceNotFound
            title="Account members not found"
            description={<p>Members not found</p>}
            returnTo="/accounts"
            returnLabel="Go back to accounts"
          />
        );
      default:
        return <LoadingError title="Failed to load members" />;
    }
  }

  const handlePaginateMembers = (url: string) => {
    const vitalstatistixBaseUrl = baseUrl();
    const urlObject = new URL(url, vitalstatistixBaseUrl);
    const offset = urlObject.searchParams.get('offset');
    if (navigate) {
      if (offset) {
        navigate(`/accounts/${account._id}/members?offset=${offset}`);
      } else {
        navigate(`/accounts/${account._id}/members`);
      }
    }
    setPageUrl(url);
  };

  const handleSearchMembers = (term: string) => {
    const t = (term || '').trim();
    setSearchTerm(t);
    if (t.length === 0) {
      // Reset pagination when clearing search
      setPageUrl(undefined);
    }
  };

  const handleRoleFilterChange = (filter: string) => {
    if (filter.trim().length >= FILTER_MIN_LENGTH || filter.trim().length === 0) {
      setRoleFilter(filter);
      setPageUrl(undefined);
    }
  };

  const handleTeamFilterChange = (filter: string) => {
    if (filter.trim().length >= FILTER_MIN_LENGTH || filter.trim().length === 0) {
      setTeamFilter(filter);
      setPageUrl(undefined);
    }
  };

  const handleResendInvitation = (memberId: string) => {
    dispatch(Actions.resendMemberInvitation(account, memberId));
    Notifications.show({ intent: Intent.SUCCESS, message: 'Invitation resent!' });
  };

  const getMemberSession = (memberId: string) => memberSessions.filter((s) => s.memberId === memberId);
  const getMemberLastActive = (memberId: string, dateFormat: string) => {
    const ms = getMemberSession(memberId);
    if (ms.length) {
      if (dateFormat === 'MDY') {
        return format(ms[0].lastActiveDate, 'MM/dd/yyyy');
      }
      if (dateFormat === 'timeAgo') {
        return formatDistanceToNow(ms[0].lastActiveDate, { addSuffix: true });
      }
    }
    return 'No activity';
  };

  const customRolesForMemberInTeams = (member: Member, roles: { [accountId: string]: CustomRole }) => {
    const roleKeysFromTeams = member.teams
      ? member.teams.map((t: TeamsData) => t.customRoleKeys).reduce((acc: string[], keys) => acc.concat(keys), [])
      : [];
    return roles ? Object.values(roles).filter((r: CustomRole) => roleKeysFromTeams.includes(r.key)) : [];
  };

  // New function that returns role-team pairs to maintain the team-role relationship
  const customRolesWithTeamsForMember = (member: Member, customRolesInput: { [accountId: string]: CustomRole }) => {
    const roleTeamPairs: Array<{ role: CustomRole; team: TeamsData }> = [];

    if (member.teams) {
      member.teams.forEach((team: TeamsData) => {
        team.customRoleKeys.forEach((roleKey: string) => {
          const role = Object.values(customRolesInput).find((r: CustomRole) => r.key === roleKey);
          if (role) {
            roleTeamPairs.push({ role, team });
          }
        });
      });
    }

    return roleTeamPairs;
  };

  const renderExportAsCsv = () => {
    const exportData = exportQuery.data;
    const exportMembers: Member[] = exportData?.entities?.members
      ? (Object.values(exportData.entities.members) as Member[])
      : [];
    const headerRows = ['Name', 'Last active', 'ID'];
    const memberRows = exportMembers.map((m) => [getMemberDisplayName(m), getMemberLastActive(m._id, 'MDY'), m._id]);
    const fileName = `${getAccountDisplayName(account)}-Members`;
    return (
      <CSVExport
        fileName={fileName}
        headerRows={headerRows}
        tableRows={memberRows}
        buttonText="Export All Members as CSV"
      />
    );
  };

  return (
    <>
      <MembersPanel
        account={account}
        settings={settings}
        scopes={scopes}
        memberSessions={memberSessions}
        members={sortedMembers}
        customRoles={customRoles}
        onSetMfaDisabled={onSetMfaDisabled}
        handlePaginateMembers={handlePaginateMembers}
        handleSearchMembers={handleSearchMembers}
        handleRoleFilterChange={settings.enableRoleAndTeamFilter ? handleRoleFilterChange : () => {}}
        handleTeamFilterChange={settings.enableRoleAndTeamFilter ? handleTeamFilterChange : () => {}}
        handleResendInvitation={handleResendInvitation}
        onMembersRefresh={() => membersListQuery.refetch()}
        getMemberSession={getMemberSession}
        getMemberLastActive={getMemberLastActive}
        customRolesForMemberInTeams={customRolesForMemberInTeams}
        customRolesWithTeamsForMember={customRolesWithTeamsForMember}
        paginationData={paginationData}
        isFetching={isFetching}
        roleFilter={roleFilter}
        teamFilter={teamFilter}
        fetchProgress={fetchProgress}
        isFilterActive={hasActiveFilters}
        filterHitCap={filterHitCap}
        filterResultCap={FILTERED_RESULTS_LIMIT}
        marketplaceProvider={marketplaceProvider}
      />
      {settings.enableMemberSessions && renderExportAsCsv()}
    </>
  );
}
